
import { defineComponent, PropType } from 'vue'

import {
  CompiledPinnedReportT,
  ILoadingValue,
  IReportMetadata,
  LoadingConditions,
  ReportMeta,
  ReportPageT
} from '~/types/reportTypes'
import { ReportLevel } from '~/types/types'
import { getFilterType } from '~/utils/reports'
import { shortenLargeNumber } from '~/utils/string'
import UIDistributionBar from '~/components/Common/UI2/UIDistributionBar.vue'

interface VulnerabilityMapping {
  name: string
  color: string
  order: number
}

interface VulnerabilityMappings {
  [key: string]: VulnerabilityMapping
}

interface SeverityItem {
  slug: string
  name: string
  color: string
  count: number
  order: number
}

interface VulnerabilityDatum {
  slug: string
  value?: number
}

export default defineComponent({
  name: 'PinnedActive3rdPartyVulns',
  components: {
    UIDistributionBar
  },
  props: {
    allowPinningReports: {
      type: Boolean,
      required: true
    },
    error: Boolean,
    reportKey: {
      type: String as PropType<ReportPageT>,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    level: {
      type: String as PropType<ReportLevel>,
      required: true
    },
    loadingValue: {
      type: Object as PropType<ILoadingValue>,
      required: true
    },
    metadata: {
      type: Object as PropType<IReportMetadata | null>,
      default: null
    },
    owner: {
      type: String,
      required: true
    },
    pinnedReports: {
      type: Array as PropType<Array<CompiledPinnedReportT>>,
      required: true
    },
    provider: {
      type: String,
      required: true
    },
    repositoryGroupSlug: {
      type: String,
      default: ''
    },
    repoName: {
      type: String,
      default: ''
    },
    reportSlot: {
      type: Number,
      required: true
    },
    // TODO: Add props for vulnerability data that you'll need for visualization
    vulnerabilityData: {
      type: Array as PropType<VulnerabilityDatum[]>,
      default: () => []
    }
  },
  data() {
    return {
      revealReportControls: false,
      timeoutId: null as ReturnType<typeof setTimeout> | null,
      LoadingConditions
    }
  },
  computed: {
    helpText(): string {
      const { helpText } = ReportMeta[this.reportKey]
      return helpText ?? ''
    },

    isReportWidgetDataFetch(): boolean {
      return this.loadingValue.condition === LoadingConditions.REPORT_WIDGET_DATA_FETCH
    },

    isReportGettingSwapped(): boolean {
      return this.loadingValue.condition === LoadingConditions.REPORT_SWAP
    },

    reportControlsWidth(): string {
      if (this.allowPinningReports) {
        return 'w-72 lg:w-80 xl:w-72'
      }
      return 'w-60 lg:w-72 xl:w-60'
    },

    reportType(): string {
      const reportType = this.metadata?.filter || this.reportKey

      if (this.loadingValue.status || !reportType) {
        return ''
      }
      return reportType
    },

    hasData(): boolean {
      return (
        Array.isArray(this.vulnerabilityData) &&
        this.vulnerabilityData.length > 0 &&
        this.vulnerabilityData.some((item) => (item.value ?? 0) > 0)
      )
    },

    severityMappings(): VulnerabilityMappings {
      return {
        cri: { name: 'Critical', color: 'cherry-500', order: 1 },
        hig: { name: 'High', color: 'amber', order: 2 },
        med: { name: 'Medium', color: 'honey', order: 3 },
        low: { name: 'Low', color: 'vanilla-400', order: 4 }
      }
    },

    reachabilityMappings(): VulnerabilityMappings {
      return {
        rea: { name: 'Reachable', color: 'cherry-500', order: 1 },
        unr: { name: 'Unreachable', color: 'honey-500', order: 2 },
        unk: { name: 'Unknown', color: 'vanilla-100', order: 3 }
      }
    },

    isReachabilityFilter(): boolean {
      const filterType = this.metadata?.filter || ''
      return filterType.toLowerCase().includes('reachability')
    },

    currentMappings(): VulnerabilityMappings {
      return this.isReachabilityFilter ? this.reachabilityMappings : this.severityMappings
    },

    distributionBarItems() {
      if (!this.vulnerabilityData || !Array.isArray(this.vulnerabilityData)) {
        return []
      }
      return this.vulnerabilityData
        .map((item) => ({
          key: item.slug,
          value: item.value ?? 0,
          color: this.currentMappings[item.slug]?.color || 'vanilla-400'
        }))
        .filter((item) => item.value > 0)
        .sort((a, b) => {
          const orderA = this.currentMappings[a.key]?.order || 999
          const orderB = this.currentMappings[b.key]?.order || 999
          return orderA - orderB
        })
    },

    allSeverities(): SeverityItem[] {
      if (!this.vulnerabilityData || !Array.isArray(this.vulnerabilityData)) {
        return Object.keys(this.currentMappings)
          .map((key) => ({
            slug: key,
            name: this.currentMappings[key].name,
            color: this.currentMappings[key].color,
            count: 0,
            order: this.currentMappings[key].order
          }))
          .sort((a, b) => a.order - b.order)
      }

      // Create a map of existing data
      const dataMap = this.vulnerabilityData.reduce<Record<string, number>>((acc, item) => {
        acc[item.slug] = item.value ?? 0
        return acc
      }, {})

      // Return all mappings with actual counts or 0
      return Object.keys(this.currentMappings)
        .map((key) => ({
          slug: key,
          name: this.currentMappings[key].name,
          color: this.currentMappings[key].color,
          count: dataMap[key] || 0,
          order: this.currentMappings[key].order
        }))
        .sort((a, b) => a.order - b.order)
    },

    totalCount(): number {
      return this.allSeverities.reduce((sum, item) => sum + item.count, 0)
    }
  },
  methods: {
    shortenLargeNumber,

    hideReportControls(): void {
      this.timeoutId = setTimeout(() => {
        this.revealReportControls = false
      }, 1000)
    },

    navigateToReportPage(): void {
      const filterType = getFilterType(this.metadata?.filter)

      const atRepoLevel = this.level === ReportLevel.Repository

      const reportPageUrl = {
        name: atRepoLevel
          ? `provider-owner-repo-reports-${this.reportKey}`
          : `orgs-provider-owner-reports-${this.reportKey}`,
        params: this.$route.params,
        query: {
          filter: filterType
        }
      }

      this.$router.push(reportPageUrl)
    },

    showReportControls(): void {
      if (this.timeoutId) {
        clearTimeout(this.timeoutId)
      }
      this.revealReportControls = true
    }
  }
})
